import { useCallback, useEffect, useState } from 'react';
import { BellRing, FileWarning } from 'lucide-react';
import { Card, CardContent } from '../ui/Card';
import timesheetApprovalService, { type UnsubmittedPerson, type UnsubmittedStaff } from '../../services/timesheetApproval';
import { formatDateTimeInTz, formatLocalDateInTz, useOrgTimezone } from '../../utils/timezone';
import { cn } from '../../utils/cn';

const nameOf = (p: UnsubmittedPerson) => [p.first_name, p.last_name].filter(Boolean).join(' ') || p.username;
const h = (n: number) => `${Number(n.toFixed(2))} h`;

/**
 * A supervisor's own staff with time in a finished pay cycle that is not
 * submitted, or was sent back to fix, with a Nudge each (and for everyone).
 * A nudge is an in-app notification and push to the employee that opens
 * their timesheet. Renders nothing while nobody is behind.
 */
export default function UnsubmittedStaffPanel() {
  const orgTz = useOrgTimezone();
  const [data, setData] = useState<UnsubmittedStaff | null>(null);
  const [busy, setBusy] = useState<number | 'all' | null>(null);
  const [message, setMessage] = useState<{ text: string; error?: boolean } | null>(null);

  const load = useCallback(() => {
    timesheetApprovalService
      .getUnsubmittedStaff()
      .then(res => { if (res.success) setData(res.data); })
      // A supervisor without the data still has the approvals below; say nothing.
      .catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!data || data.people.length === 0) return null;

  const cooldownMs = (data.cooldown_minutes ?? 60) * 60 * 1000;
  const recent = (p: UnsubmittedPerson) =>
    !!p.last_nudged_at && Date.now() - new Date(p.last_nudged_at).getTime() < cooldownMs;
  const nudgeable = data.people.filter(p => !recent(p));

  const nudge = async (ids: number[], key: number | 'all') => {
    setBusy(key);
    setMessage(null);
    try {
      const res = await timesheetApprovalService.nudgeToSubmit(ids);
      if (!res.success) throw new Error('The nudge could not be sent.');
      const { sent, skipped, nudged_at } = res.data;
      const sentIds = new Set(sent.map(s => s.user_id));
      setData(d => d && {
        ...d,
        people: d.people.map(p => (sentIds.has(p.user_id) ? { ...p, last_nudged_at: nudged_at } : p)),
      });
      const parts: string[] = [];
      if (sent.length) parts.push(`Nudged ${sent.length === 1 ? sent[0].name : `${sent.length} people`}.`);
      if (skipped.length) parts.push(`${skipped.length} skipped: ${skipped.map(s => `${s.name ?? 'someone'} (${s.reason.toLowerCase()})`).join(', ')}.`);
      setMessage({ text: parts.join(' ') || 'Nobody needed a nudge.' });
    } catch (err: unknown) {
      setMessage({ text: err instanceof Error ? err.message : 'The nudge could not be sent.', error: true });
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card className="mb-6">
      <CardContent className="p-4 space-y-3">
        <div className="flex flex-wrap items-start gap-3">
          <FileWarning className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-neutral-900">
              Not submitted yet: {data.people.length} {data.people.length === 1 ? 'person' : 'people'}
            </p>
            <p className="text-sm text-neutral-600 break-words">
              Your staff with time in a finished pay cycle (last {data.lookback_days} days) that is not submitted or was sent back.
            </p>
          </div>
          {data.people.length > 1 && (
            <button
              type="button"
              onClick={() => void nudge(nudgeable.map(p => p.user_id), 'all')}
              disabled={busy !== null || nudgeable.length === 0}
              className="shrink-0 inline-flex items-center gap-1.5 rounded-md bg-amber-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <BellRing className="h-4 w-4" />
              {busy === 'all' ? 'Sending…' : `Nudge all (${nudgeable.length})`}
            </button>
          )}
        </div>

        {message && (
          <p className={cn('text-sm break-words', message.error ? 'text-red-700' : 'text-emerald-700')}>{message.text}</p>
        )}

        <ul className="divide-y divide-neutral-100 border border-neutral-200 rounded-lg">
          {data.people.map(p => {
            const range = p.first_date === p.last_date
              ? formatLocalDateInTz(p.first_date, orgTz)
              : `${formatLocalDateInTz(p.first_date, orgTz)} – ${formatLocalDateInTz(p.last_date, orgTz)}`;
            const parts: string[] = [];
            if (p.not_submitted_hours > 0) parts.push(`${h(p.not_submitted_hours)} not submitted`);
            if (p.sent_back_hours > 0) parts.push(`${h(p.sent_back_hours)} sent back`);
            const isRecent = recent(p);
            return (
              <li key={p.user_id} className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2 text-sm">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-neutral-900 break-words">{nameOf(p)}</p>
                  <p className="text-neutral-600 break-words">{range} · {parts.join(' · ')}</p>
                  {p.sent_back_hours > 0 && p.rejection_reason && (
                    <p className="text-red-700 break-words">Sent back: {p.rejection_reason}</p>
                  )}
                  {p.last_nudged_at && (
                    <p className="text-xs text-neutral-500">Nudged {formatDateTimeInTz(p.last_nudged_at, orgTz)}</p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => void nudge([p.user_id], p.user_id)}
                  disabled={busy !== null || isRecent}
                  title={isRecent ? 'Nudged within the last hour' : undefined}
                  aria-label={`Nudge ${nameOf(p)}`}
                  className="shrink-0 inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-white px-2.5 py-1 text-xs font-medium text-amber-900 hover:bg-amber-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <BellRing className="h-3.5 w-3.5" />
                  {busy === p.user_id ? 'Sending…' : isRecent ? 'Nudged' : 'Nudge'}
                </button>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}
